import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchDecisions, fetchHomeVolume, type DecisionRecord, type HomeProject } from '../api/home.api';
import { densityRange, volumeRange } from '../lib/buckets';
import { fetchAgendaProjects } from '@/features/projects';
import { queryKeys } from '@/shared/lib/queryKeys';
import { toDateInputValue } from '@/shared/lib/dates';
import type { AgendaProject } from '@/shared/types/domain';

/**
 * As três consultas da Início.
 *
 * `staleTime` longo nas três, e o motivo é o mesmo: **elas olham para janelas,
 * não para o estado da esteira.** O que muda a cada aprovação já chega pelos
 * blocos que leem `projectStatusCounts` e a fila — recarregar dez meses de
 * histórico a cada transição seria trabalho que ninguém pediu, e é por isso que
 * as chaves ficam fora do prefixo `['projects']` que
 * `invalidateAfterTransition` derruba inteiro.
 *
 * ⚠️ **A janela é calculada UMA vez por render e entra na chave.** Passar `now`
 * de fora, em vez de chamar `new Date()` dentro da `queryFn`, é o que impede a
 * consulta de mudar de recorte entre um refetch e outro — e o que faz a virada
 * do mês produzir uma chave nova em vez de servir o cache com a coluna "agora"
 * no lugar errado.
 */

/** O passado não muda; o futuro próximo muda devagar. */
const WINDOW_STALE_TIME = 5 * 60_000;

/** Quantos dias de decisões alimentam a mediana (PRD §6.3). */
export const PIPELINE_WINDOW_DAYS = 90;

export function useHomeVolume(now: Date): UseQueryResult<HomeProject[]> {
  const range = volumeRange(now);
  return useQuery({
    queryKey: queryKeys.home.volume(range),
    queryFn: () => fetchHomeVolume(range.from, range.to),
    staleTime: WINDOW_STALE_TIME,
  });
}

/**
 * A densidade das próximas oito semanas — **a filial inteira, para os cinco
 * papéis** ([ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)).
 *
 * É o único bloco da Início cujo escopo não é o alcance de leitura da pessoa, e
 * é por isso que a legenda dele diz "na igreja" enquanto os outros dizem "no seu
 * alcance". Vem da mesma RPC que o calendário já usa: contar o que aquela pessoa
 * **já vê na grade** é estritamente menos informação do que a grade.
 */
export function useHomeDensity(now: Date): UseQueryResult<AgendaProject[]> {
  const range = densityRange(now);
  return useQuery({
    queryKey: queryKeys.home.density(range),
    queryFn: () => fetchAgendaProjects({ from: range.from, to: range.to }),
    staleTime: WINDOW_STALE_TIME,
  });
}

/**
 * As decisões da janela recente, para a saúde da esteira.
 *
 * `enabled` porque só quem tem `reviewProject` vê o bloco — e uma consulta que
 * ninguém vai exibir é uma ida ao servidor por nada. **Não é isso que protege o
 * dado**: quem protege é a RLS, que já recortaria a resposta pela filial de quem
 * pergunta. Aqui é só economia.
 */
export function useHomePipeline(now: Date, enabled: boolean): UseQueryResult<DecisionRecord[]> {
  // O dia, e não o instante: com o timestamp cheio a chave mudaria a cada
  // render e a consulta nunca acertaria o cache.
  const since = `${toDateInputValue(
    new Date(now.getTime() - PIPELINE_WINDOW_DAYS * 86_400_000),
  )}T00:00:00.000Z`;

  return useQuery({
    queryKey: queryKeys.home.pipeline(since),
    queryFn: () => fetchDecisions(since),
    enabled,
    staleTime: WINDOW_STALE_TIME,
  });
}
